import React from 'react'
import { Box, Select, MenuItem, OutlinedInput, Chip, Typography, useTheme, useMediaQuery } from '@mui/material'
import { RestartAlt } from '@mui/icons-material'
import CustomButton from '../CustomButton'
import CustomAccordion from '../CustomAccordion'
import DateRangePicker from '../DateRangePicker'
import { formatDate } from '../../../utils/helper'

interface FilterOption {
  label: string
  value: string
}

interface DateFiltersProps {
  // Date filter props
  fromDate: Date | string | null
  toDate: Date | string | null
  onDateRangeChange: (fromDate: Date | null, toDate: Date | null) => void
  applyDateFilter: (fromDate: Date, toDate: Date) => void
  chipTitle: string
  // Platform filter props
  sourceType?: string
  onSourceTypeChange?: (value: string) => void
  platformOptions?: FilterOption[]
  dataSourceType?: string
  // Utility props
  onReset: () => void

  // Optional customization
  className?: string
  showPlatformFilter?: boolean
  dateFilterTitle?: string
  onDataSourceChange?: (value: string) => void
  showDataSourceFilter?: boolean
}

const defaultPlatformOptions: FilterOption[] = [
  { label: 'All', value: '' },
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'nAI', value: 'nai' },
]

const dashboardataSource: FilterOption[] = [
  { label: 'All', value: 'all' },
  { label: 'Current Subsidiary', value: 'owner' },
  { label: 'Linked Projects', value: 'linked_projects' },
]

const DateFilters: React.FC<DateFiltersProps> = ({
  fromDate,
  toDate,
  onDateRangeChange,
  applyDateFilter,
  sourceType,
  onSourceTypeChange,
  dataSourceType,
  onDataSourceChange,
  platformOptions = defaultPlatformOptions,
  onReset,
  chipTitle,
  className = '',
  showPlatformFilter = true,
  dateFilterTitle = 'Filter By Date',
  showDataSourceFilter = false,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))

  const handleDateRangeReset = () => {
    onDateRangeChange(null, null)
  }

  const selectSxStyles = {
    '& .MuiOutlinedInput-notchedOutline': {
      padding: '0 !important',
      margin: '0 !important',
      borderRadius: '8px',
    },
  }

  const DateRangeSection = () => (
    <Box className="flex sm:items-center w-full">
      {fromDate && toDate ? (
        <Chip
          className="!ml-2"
          label={`${chipTitle} from ${formatDate(fromDate, 'DD/MM/YYYY')} to ${formatDate(toDate, 'DD/MM/YYYY')}`}
          variant="outlined"
          onClick={handleDateRangeReset}
          onDelete={handleDateRangeReset}
        />
      ) : (
        <DateRangePicker applyDateFilter={applyDateFilter} />
      )}
    </Box>
  )

  const PlatformSection = () => (
    <Box className="flex items-center flex-col w-full lg:flex-row lg:w-auto">
      <Box className="flex items-center justify-start w-full mt-2 lg:mt-0">
        <Typography variant="body2" className="whitespace-nowrap">
          Platform:
        </Typography>
        <Select
          size="small"
          className="w-[195px] text-textSecondary mx-2"
          displayEmpty
          value={sourceType || ''}
          onChange={(e) => (onSourceTypeChange ? onSourceTypeChange(e.target.value) : null)}
          input={<OutlinedInput />}
          inputProps={{ 'aria-label': 'Platform select' }}
          sx={selectSxStyles}
        >
          <MenuItem disabled value="">
            <em>Select Platform</em>
          </MenuItem>
          {platformOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  )

  const ResetButton = ({ mobile = false }) =>
    mobile ? (
      <CustomButton onClick={onReset} variant="outlined" className="h-10 !mt-2 !ml-2 !w-full">
        RESET
      </CustomButton>
    ) : (
      <Typography
        onClick={onReset}
        className="underline cursor-pointer flex items-center gap-1"
        color="primary"
        component="button"
        sx={{
          background: 'none',
          border: 'none',
          padding: 0,
          '&:hover': { opacity: 0.8 },
        }}
      >
        RESET
        <RestartAlt fontSize="small" />
      </Typography>
    )

  const CustomDataSourceSelect = () => (
    <Box className="flex items-center flex-col w-full lg:flex-row lg:w-auto">
        <Box className="flex items-center justify-start w-full mt-2 lg:mt-0">
          <Typography variant="body2" className="whitespace-nowrap">
            Source:
          </Typography>
          <Select
            size="small"
            className="w-[195px] text-textSecondary mx-2"
            displayEmpty
            value={dataSourceType || ''}
            onChange={(e) => (onDataSourceChange ? onDataSourceChange(e.target.value) : null)}
            input={<OutlinedInput />}
            inputProps={{ 'aria-label': 'Platform select' }}
            sx={selectSxStyles}
          >
            <MenuItem disabled value="">
              <em>Select Source</em>
            </MenuItem>
            {dashboardataSource.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
  )

  if (isMobile) {
    return (
      <Box className={`flex !w-full mb-2 ${className}`}>
        <CustomAccordion className="w-full" title={dateFilterTitle}>
          <Box className="flex flex-wrap justify-between !w-full gap-4">
            <DateRangeSection />
            {showPlatformFilter ? <PlatformSection /> : null}
            {showDataSourceFilter ? <CustomDataSourceSelect /> : null}
            <ResetButton mobile />
          </Box>
        </CustomAccordion>
      </Box>
    )
  }

  return (
    <Box className={`p-4 pt-0 hidden lg:flex flex-wrap items-center gap-4 ${className}`}>
      <Box className="flex sm:items-center">
        <DateRangePicker applyDateFilter={applyDateFilter} />
        {fromDate && toDate && (
          <Chip
            className="!ml-2"
            label={`${chipTitle} from ${formatDate(fromDate, 'DD/MM/YYYY')} to ${formatDate(toDate, 'DD/MM/YYYY')}`}
            variant="outlined"
            onClick={handleDateRangeReset}
            onDelete={handleDateRangeReset}
          />
        )}
      </Box>

      <Box className="flex items-center gap-2 justify-end ml-auto">
        {showPlatformFilter ? <PlatformSection /> : null}
      </Box>
      {showDataSourceFilter ? (
        <Box className="justify-end">
          <CustomDataSourceSelect />
        </Box>
      ) : null}
      <Box className="justify-end"><ResetButton /></Box>
    </Box>
  )
}

export default DateFilters
